/**
 * Wave tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const waveTattoosPack: ToilePack = {
    slug: 'wave-tattoos',
    category: 'tattoo',
    name: 'Wave tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'great', label: 'Great wave', subject: 'a great curling wave with claw-like foam at its crest, a smaller wave below and a distant mountain under the curve'},
        {key: 'circle', label: 'Wave in circle', subject: 'a single wave rolling inside a thin circle, the crest curling over toward the right'},
        {key: 'sunset', label: 'Wave and sun', subject: 'a wave rising in the foreground with a half sun setting on the horizon line behind it'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, heavy solid black masses with the details cut out of them as bare paper', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a koi', 'blue ink', 'ribs'],
    directionHint: 'try: a koi',
    seo: {
        title: 'Wave tattoo designs: great wave, circle wave, wave and sun',
        metaDescription: 'Nine wave tattoo designs: a great curling wave, a wave in a circle and a wave under a setting sun, in three styles.',
        h1: 'Wave tattoo designs: great curling wave, wave in a circle, wave and sun',
        tagline: 'Three waves, each drawn in fine line, blackwork and dotwork on white paper.',
        intro: "Wave tattoo designs, for people who love the sea, worn on the forearm, the ankle or the ribs. The nine sheets show a great curling wave in the manner of Hokusai's Great Wave off Kanagawa, a wave rolling inside a circle, and a wave under a setting sun. Each is drawn as fine line, blackwork and dotwork. Type a different sea creature or twist, such as a koi, and the same styles can be asked for.",
        useCases: [
            'a forearm or rib piece with a great wave',
            'a small wave in a circle for the ankle',
            'a seascape with a setting sun',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine wave designs: a great curling wave, a wave in a circle and a wave with a sun, each in three styles.'},
            {q: 'Is the great wave copied from Hokusai?', a: "The first design follows the manner of Hokusai's print The Great Wave off Kanagawa; it is a new drawing in that style, not a copy of the print."},
            {q: 'Can I ask for a koi or a different colour?', a: 'Yes. Type a different sea creature or twist, such as a koi or blue ink; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Fine line is one uniform hairline with little shading. Blackwork uses heavy solid black masses. Dotwork builds the shadows from dots.'},
        ],
    },
}
